import React from "react";
import Select from "react-select";
import { Modal } from "../../../../../components/common/Modal";
import { Button } from "../../../../../components/common/Button";
import { RootState } from "../../../../../store";
import { UserActionCreator } from "../../../../../store/userActions";
import { connect } from "react-redux";
import { useAttachCalcToDealModal } from "./hooks/useAttachCalcToDealModal";
import { useSelectMenuProps } from "./hooks/useSelectMenuProps";
import { Skeleton } from "../../../../../components/common/Skeleton"; // 👈 добавили

type DealStatus = {
    id: number;
    code?: string;
    name: string;
};

type AttachModalOwnProps = {
    isOpen: boolean;
    onClose: () => void;
    sharingId: number | string | null; // attachmentableId (id расчёта / подборки)
};

type AttachModalStateProps = {
    clientsPending: boolean;
    clientsLoadingMore: boolean;
    clientsError: boolean;
    clients: Array<{
        id: number | string;
        fullname?: string;
        phone?: string | number;
    }>;
    clientsMeta: {
        perPage: number;
        nextCursor: string | null;
        hasMore: boolean;
    };
    dealsMap: Record<string | number, any[]>;
    dealsPending: boolean;
    dealsError: boolean;
    dealsLoadingId: number | string | null;
    dealStatuses: DealStatus[];
    dealStatusesPending: boolean;
};

type AttachModalDispatchProps = {
    getClientsBase: (payload?: {
        sortBy?: "fullname";
        perPage?: number;
        cursor?: string | null;
    }) => any;
    getClientDeals: (payload: { clientId: number | string }) => any;
    attachClientDealAttachment: (payload: {
        dealId: number | string;
        attachmentableId: number | string;
        attachmentableType: string;
    }) => any;
    getDealAttachments: (payload: {
        clientId: number | string;
        dealId: number | string;
    }) => any;
    createClientInBase: (payload: {
        fullname: string;
        phone: string;
        telegram?: string;
    }) => any;
    createClientDeal: (payload: {
        dealStatusId: number;
        clientId: number;
        query: string;
    }) => any;
    getDealStatuses: () => any;
};

type Props = AttachModalOwnProps &
    AttachModalStateProps &
    AttachModalDispatchProps;

const selectStyles = {
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 1100,
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 1100,
    }),
};

const _AttachCalcToDealModal: React.FC<Props> = (props) => {
    const {
        isOpen,
        onClose,
        sharingId,
        clientsPending,
        clientsError,
        dealsPending,
        dealsError,
        dealsLoadingId,
    } = props;

    const {
        clientId,
        dealId,
        attachPending,
        clientOptions,
        dealOptions,
        handleClientChange,
        handleDealChange,
        handleAttach,
        hasDealsEntryForSelectedClient,

        // ✅ режим
        mode,

        // ✅ создание клиента
        newClientFullName,
        newClientPhone,
        createClientErrorMessage,
        setNewClientFullName,
        setNewClientPhone,
        createClientPending,
        cancelCreateClient,
        handleCreateClient,

        // ✅ создание сделки
        newDealQuery,
        setNewDealQuery,
        defaultDealStatusId,
        createDealPending,
        cancelCreateDeal,
        handleCreateDeal,
    } = useAttachCalcToDealModal(props);

    const { selectMenuProps } = useSelectMenuProps();

    const isDealsLoadingForClient =
        clientId &&
        !hasDealsEntryForSelectedClient && // ещё нет записи — значит первый запрос
        dealsPending &&
        String(dealsLoadingId) === String(clientId);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Прикрепить расчёт к сделке"
            text=""
            actionButtonText={null}
        >
            {/* на мобиле — свободная высота, на десктопе режем по 70vh */}
            <div className="space-y-4 md:max-h-[70vh] md:overflow-y-auto pr-1">
                {/* CLIENT SELECT / CREATE */}
                <div>
                    <div className="text-sm mb-1">Клиент</div>

                    {clientsError && (
                        <div className="text-xs text-red-500 mb-1">Ошибка загрузки</div>
                    )}

                    {mode === "createClient" ? (
                        <div className="space-y-2">
                            <input
                                className="w-full h-9 rounded-md border px-3 text-sm"
                                placeholder="ФИО (обязательно)"
                                value={newClientFullName}
                                onChange={(e) => setNewClientFullName(e.target.value)}
                            />
                            <input
                                className="w-full h-9 rounded-md border px-3 text-sm"
                                placeholder="Телефон (обязательно)"
                                value={newClientPhone}
                                onChange={(e) => setNewClientPhone(e.target.value)}
                            />

                            {createClientErrorMessage && (
                                <div className="text-xs text-red-600" role="alert">
                                    {createClientErrorMessage}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <Button text="Назад" variant="secondary" onClick={cancelCreateClient} />
                                <Button
                                    text={createClientPending ? "Создаём…" : "Создать клиента"}
                                    onClick={handleCreateClient}
                                    disabled={
                                        !newClientFullName.trim() ||
                                        !newClientPhone.trim() ||
                                        createClientPending
                                    }
                                    pending={createClientPending}
                                />
                            </div>
                        </div>
                    ) : clientsPending && !clientOptions.length ? (
                        <Skeleton className="h-9 rounded-md" />
                    ) : (
                        <Select
                            placeholder="Выберите клиента"
                            options={clientOptions}
                            value={clientOptions.find((o) => String(o.value) === String(clientId)) || null}
                            onChange={handleClientChange}
                            isClearable
                            isSearchable
                            classNamePrefix="hh-select"
                            styles={selectStyles}
                            {...selectMenuProps}
                        />
                    )}
                </div>

                {/* DEAL SELECT / CREATE */}
                {clientId && mode !== "createClient" && (
                    <div>
                        <div className="text-sm mb-1">Сделка</div>

                        {dealsError && (
                            <div className="text-xs text-red-500 mb-1">Ошибка загрузки сделок</div>
                        )}

                        {mode === "createDeal" ? (
                            <div className="space-y-2">
                                <input
                                    className="w-full h-9 rounded-md border px-3 text-sm"
                                    placeholder="Название сделки (например: Квартира 3.3 млн / 24 мес)"
                                    value={newDealQuery}
                                    onChange={(e) => setNewDealQuery(e.target.value)}
                                />

                                {!defaultDealStatusId && (
                                    <div className="text-xs text-gray-500">Загружаем статусы сделок…</div>
                                )}

                                <div className="flex gap-3">
                                    <Button text="Назад" variant="secondary" onClick={cancelCreateDeal} />
                                    <Button
                                        text={createDealPending ? "Создаём…" : "Создать сделку"}
                                        onClick={handleCreateDeal}
                                        disabled={!defaultDealStatusId || createDealPending}
                                        pending={createDealPending}
                                    />
                                </div>
                            </div>
                        ) : isDealsLoadingForClient ? (
                            <Skeleton className="h-9 rounded-md" />
                        ) : hasDealsEntryForSelectedClient && !dealOptions.length ? (
                            <div className="text-xs text-gray-500">Нет сделок для этого клиента</div>
                        ) : (
                            <Select
                                placeholder="Выберите сделку"
                                options={dealOptions}
                                value={dealOptions.find((o) => String(o.value) === String(dealId)) || null}
                                onChange={handleDealChange}
                                isClearable
                                isSearchable
                                noOptionsMessage={() => (dealsPending ? "Загружаем…" : "Нет сделок")}
                                classNamePrefix="hh-select"
                                styles={selectStyles}
                                {...selectMenuProps}
                            />
                        )}
                    </div>
                )}


                {/* BUTTONS */}
                <div className="flex gap-8 mt-2">
                    <Button text="Отмена" variant="secondary" onClick={onClose} />
                    <Button
                        text={attachPending ? "Прикрепляем…" : "Прикрепить расчёт"}
                        onClick={handleAttach}
                        disabled={
                            !clientId ||
                            !dealId ||
                            attachPending ||
                            !sharingId ||
                            mode !== "select"
                        }
                        pending={attachPending}
                    />
                </div>
            </div>
        </Modal>
    );
};

const mapStateToProps = (state: RootState): AttachModalStateProps => {
    const userState: any = (state as any).userReducer || {};

    const clientsBase = userState.clientsBase || {};
    const clientDeals = userState.clientDeals || {};
    const dealStatuses = userState.dealStatuses || {};

    return {
        clientsPending: !!clientsBase.pending,
        clientsLoadingMore: !!clientsBase.loadingMore,
        clientsError: !!clientsBase.error,
        clients: clientsBase.data || [],
        clientsMeta: clientsBase.meta || {
            perPage: 100,
            nextCursor: null,
            hasMore: false,
        },

        dealsMap: clientDeals.data || {},
        dealsPending: !!clientDeals.pending,
        dealsError: !!clientDeals.error,
        dealsLoadingId: clientDeals.currentLoadingId ?? null,
        dealStatuses: dealStatuses.data || [],
        dealStatusesPending: !!dealStatuses.pending,
    };
};

const mapDispatchToProps: AttachModalDispatchProps = {
    getClientsBase: UserActionCreator.getClientsBase as any,
    getClientDeals: UserActionCreator.getClientDeals as any,
    attachClientDealAttachment:
        UserActionCreator.attachClientDealAttachment as any,
    getDealAttachments: UserActionCreator.getDealAttachments as any,
    createClientInBase: UserActionCreator.createClientInBase as any,
    createClientDeal: UserActionCreator.createClientDeal as any,
    getDealStatuses: UserActionCreator.getDealStatuses as any,
};

export const AttachCalcToDealModal = connect(
    mapStateToProps,
    mapDispatchToProps,
)(_AttachCalcToDealModal);
